import { lazy, Suspense, useCallback, useContext, useEffect, useRef, useState, type ComponentType } from "react";
import { flush, track } from "../track";
import Sheet from "../ui/Sheet";
import { toast } from "../ui/Toast";
import { Ladder, LateLine, expressNote } from "./Facts";
import { KIND_KEY, TAGGED_SIZE, openingSize } from "./kinds";
import SizeScale from "./SizeScale";
import ChristmasBase, { baseBlocks, chosenBase } from "./ChristmasBase";
import { baseKindOf, takesBase } from "./christmas";
import { checkoutResult, PageContext, type BaseChoice, type CheckoutResult } from "./page";
import ExpressChoice, { ExpressHold, useExpress } from "./ExpressChoice";
import type { PayFailure, PayFormProps } from "./PayForm";
import { hasCode, type LeadKind, type PreviewDoc, type PriceOffer } from "../types";
import type { Words } from "./copy";
import { bill, fill, MAX_UNITS } from "./multiBuy";
import { billIn, BillBox, billRows, multiBuyWords, offVars, ownsQuantity, QtyStepper, qtyKey, unbroken, unitsOf } from "./Quantity";

/** What "I want to order" opens.
 *
 *  Pressing it has already mailed them the Etsy link and the steps, and
 *  buzzed the owner. Where the page takes the payment itself, the sheet is
 *  two steps: first the size, with what it costs, how big it really is, when
 *  it would arrive and that shipping is free; then, after "Continue to
 *  payment", Stripe's form. It used to open straight on the form, and of the
 *  people who pressed the button, nine in ten left within seconds without
 *  typing a thing: the first screen after "I want to order" was an address
 *  form, asked of someone who had decided nothing yet but the size. The
 *  first step asks for one tap and no information, and remembers the size on
 *  the device, so a return visit (which is when most orders happen) opens on
 *  it. No checkout session is made until the form is asked for.
 *
 *  The Etsy steps are one tap away under either step. The one that matters
 *  there is the email in the personalisation box, which is the only thing
 *  that ties an Etsy order to this preview. Step one names the size and, for
 *  a piece with a pet, the package, with the price of every size under it, so
 *  the listing's pickers hold no surprise; and the sheet says, in every
 *  currency, that all duties and import VAT are included before Etsy shows
 *  where the parcel ships from.
 *
 *  Etsy charges euros, whoever is buying. On a page priced in pounds or
 *  dollars, which only ever happens where the page can take the payment
 *  itself, the way to Etsy names no price at all and the steps leave the
 *  ladder out: a £ figure beside a listing that will show its own conversion
 *  of a euro price would be a promise Etsy does not keep. Nothing is said
 *  about it either; the sizes and the prices are on the payment side.
 *
 *  The mail line is only drawn once the server has said what happened. Saying
 *  "I've emailed you" before the mail has gone would be the page making a
 *  promise the server might not keep. */
export type Mailed = "now" | "before" | "failed" | null;

/** Stripe's form. Only ever downloaded by someone the page offers it to.
 *
 *  A file that does not arrive is the form failing, and is said in the sheet
 *  with Etsy still underneath. Left to React it was an error nothing caught,
 *  and the whole page went blank at the moment somebody had decided to pay.
 *
 *  It happens after a deploy while the page sat open, which removes the file
 *  this build names: asking for it again gets the same missing file (Hosting
 *  answers it with the page's HTML), so "Try again" could never work. What
 *  cures it is the page itself, loaded again, which names the new file; so
 *  the sheet offers that instead, and the reload opens the sheet again by
 *  itself. React keeps whatever the first import gave it, so the failed one is
 *  still swapped for a fresh one: a sheet opened again after a mere dropped
 *  connection asks for the file again rather than failing for the rest of the
 *  visit. */
let PayForm = lazy(loadPayForm);
function loadPayForm(): Promise<{ default: ComponentType<PayFormProps> }> {
  return import("./PayForm").catch(() => {
    PayForm = lazy(loadPayForm);
    return { default: PayUnavailable };
  });
}
/** The same form, with the same fallbacks, for the "Order all" sheet. Read
 *  at render, so a failed file swapped for a fresh one above is the one used. */
export function LazyPayForm(props: PayFormProps) {
  return <PayForm {...props} />;
}
function PayUnavailable({ onFailed }: PayFormProps) {
  // Offline is only the connection: trying again once it is back works, and a
  // reload would only show the browser's own offline page.
  useEffect(() => onFailed(navigator.onLine === false ? "failed" : "stale"), [onFailed]);
  return null;
}

/** The form's own file and Stripe.js, fetched before anyone opens the sheet.
 *
 *  Making the first checkout on a page is slow because both of them have to
 *  arrive first, and the sheet sat on an empty box meanwhile: the moment the
 *  numbers page loses the most people who had decided to pay. Once per visit,
 *  and only for a page that offers paying here. No checkout session is asked
 *  for: one per visitor who never opens the sheet would only spend the cap the
 *  server keeps per page. A file that does not arrive is asked for again, here
 *  or by the sheet. */
let warmed = false;
export function warmPayment(): void {
  if (warmed) return;
  warmed = true;
  void import("./PayForm").then(
    (form) => form.warmStripe(),
    () => {
      warmed = false;
    }
  );
}

/** This page again, opening the sheet by itself as the order mail's "Pay on
 *  my page" does: on a page of several, on the card of the piece it was for,
 *  or "all" for the "Order all" sheet. Whatever else the address says (the
 *  studio's look, the mail it came from) stays as it is. */
export function reloadToPay(pieceId: string | undefined) {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("pay", "1");
    if (pieceId) url.searchParams.set("piece", pieceId);
    else url.searchParams.delete("piece");
    if (url.href === window.location.href) window.location.reload();
    else window.location.replace(url.href);
  } catch {
    window.location.reload();
  }
}

/** The Etsy steps. The last is typing the code in, which a lead sent with no
 *  code has no step for. */
const STEPS = ["stepSize", "stepEmail", "stepCode"] as const;

type View = "pick" | "pay" | "etsy" | "paid";

export default function OrderSheet({
  words: t,
  etsyUrl,
  offer,
  delivery,
  promoCode,
  mailed,
  from,
  pieceKind,
  kind,
  pieceId,
  initialSize,
  drawingUrl,
  payNow,
  onClose,
}: {
  words: Words;
  etsyUrl: string;
  offer?: PriceOffer | null;
  /** When it would arrive, on a page of one piece. */
  delivery?: PreviewDoc["delivery"];
  /** The lead's code, "" for a lead sent with none. */
  promoCode: string;
  mailed: Mailed;
  from: string;
  /** A piece's product, on a page of several, for what the Etsy tap records. */
  pieceKind?: LeadKind;
  /** The product the sizes were priced for, on any page, which the checkout
   *  is asked for by name. */
  kind?: LeadKind;
  pieceId?: string;
  /** The size tapped in a "By size" ladder, which the sheet opens on. */
  initialSize?: string;
  /** Their drawing, for the head of the first step: what they are buying. */
  drawingUrl?: string | null;
  /** Came to pay: the order mail's "Pay on my page", or a reload after a
   *  form that could not load. The sheet opens on the form, on the size they
   *  had picked, and skips the first step they have already been through;
   *  unless that step offers a Christmas base not yet chosen about in this
   *  visit (opensOn below). */
  payNow?: boolean;
  onClose: () => void;
}) {
  const page = useContext(PageContext);
  // Paying on the page, wherever the page offers it. When it is offered the
  // sheet opens on the first step: the size, then the form. The Etsy steps
  // (the email in the personalisation box, the code to copy) are the Etsy
  // path's alone; paying here needs none of them, so they are not in the way
  // of it.
  const sizes = offer?.sizes ?? [];
  const payHere = !!page?.checkout && sizes.length > 0;
  // Whether the prices in hand are the ones Etsy charges, which are euros.
  // The offer's own currency when it says, which every price since there was
  // a choice does; the page's otherwise; and euros for anything older.
  const inEuros = (offer?.currency ?? page?.currency ?? "EUR") === "EUR";
  // A lead sent with no code pays the full price: no step for it, nothing to
  // copy, and nothing to say is applied.
  const withCode = hasCode(promoCode, offer);
  const steps = withCode ? STEPS : STEPS.filter((key) => key !== "stepCode");
  // How many, where the sheet decides it: only when the server sent what the
  // bill is made of (the multi-buy, preview/multiBuy.ts), and then Stripe's
  // form no longer has a quantity of its own. Otherwise it is one here and
  // changed in Stripe's form, as it always was, and nothing below changes.
  // Chosen on the first step only: every number given to the form would be
  // a checkout session of its own. Not remembered on the device, and kept
  // through a price that changed under the form; a page read again without
  // the multi-buy is back to one.
  // Kept by the page for the rest of the visit (PageInfo.qtys), not by this
  // sheet, which is gone the moment it is closed: the button that opens it
  // again, on either step, says the number and the total, and the sheet has
  // to open on that number and the form charge for it. It used to open on
  // one, so someone who chose two, closed the sheet and came back by
  // "Continue to payment" was asked to pay for one. A piece of several keeps
  // its own.
  const owns = payHere && ownsQuantity(offer);
  const kept = qtyKey(from, pieceId);
  const onQty = page?.onQty;
  const [qty, setQtyHere] = useState(() => unitsOf(page?.qtys?.[kept]));
  const setQty = (next: number) => {
    setQtyHere(next);
    if (owns) onQty?.(kept, next);
  };
  const units = owns ? unitsOf(qty) : 1;
  // The Christmas base, where the page offers it and only when paying here
  // (ChristmasBase.tsx): switched on and its message typed on the first step,
  // kept by the page under the same key as how many, so the sheet opened
  // again by "Continue to payment" opens on it and the form is made for it.
  const baseLimits = payHere ? (page?.baseLimits ?? null) : null;
  const onBase = page?.onBase;
  const [baseChoice, setBaseHere] = useState<BaseChoice | undefined>(() => page?.bases?.[kept]);
  const setBase = (next: BaseChoice) => {
    setBaseHere(next);
    onBase?.(kept, next);
  };
  // Straight to the form when they came to pay (the order mail's link, a
  // return visit that had reached it, a reload after a form that could not
  // load), except where the page offers the Christmas base for this piece and
  // nothing has been chosen about it in this visit. The base and its message
  // are kept for the visit only (PageInfo.bases, never on the device), so a
  // return visit, or the reload, would otherwise open a form for a plain
  // statue with nothing to say the base had been left behind, and someone who
  // came from the mail would never be offered it at all. One step more, in
  // season, on a figure: the size and the base, with Continue under them.
  // The kind the base is asked by: a statue of the animals alone has its own.
  const productKind = baseKindOf(kind ?? pieceKind, offer);
  const baseUnasked = !!baseLimits && takesBase(productKind) && page?.bases?.[kept] === undefined;
  const opensOn: View = payHere ? (payNow && !baseUnasked ? "pay" : "pick") : "etsy";
  const [view, setView] = useState<View>(opensOn);
  // What the numbers page learns of this sheet, since Stripe says nothing of
  // anyone who leaves its form: when the form first showed, and, when the
  // sheet goes (closed, or the page hidden or left with it open), the
  // furthest view it reached and how long the form had been on screen by
  // then. The server reads only a close whose view is "pay" (the form's
  // longest look, or a close before it loaded), so `view` names the
  // furthest step and `at` the one they were on: "Change size" and then a
  // close must not lose a long look at the form. A close that never reached
  // "pay" leaves nothing on the lead; the numbers page reads those from the
  // absence of a checkout session (stats/compute.ts).
  const shownAt = useRef<number | null>(null);
  const viewNow = useRef(view);
  viewNow.current = view;
  const furthest = useRef<View>(view);
  if (view === "pay" || view === "paid") furthest.current = "pay";
  // Which form is on screen, by its key, for the line that says one is
  // coming: on cellular a form's first paint is where the sheet looked
  // broken, and every size change or retry is a new form.
  const [upKey, setUpKey] = useState("");
  const formKeyNow = useRef("");
  const formShown = useCallback(() => {
    setUpKey(formKeyNow.current);
    if (shownAt.current !== null) return;
    shownAt.current = Date.now();
    track("checkout.loaded");
  }, []);
  useEffect(() => {
    const opened = Date.now();
    const gone = (how: "closed" | "hidden") => {
      const at = shownAt.current;
      track("sheet.closed", {
        view: furthest.current,
        at: viewNow.current,
        how,
        ms: Date.now() - opened,
        loaded: at !== null,
        ...(at !== null ? { formMs: Date.now() - at } : {}),
      });
      // The page may be on its way out, and whatever is queued goes with it.
      flush();
    };
    const onHide = () => {
      if (document.visibilityState === "hidden") gone("hidden");
    };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      gone("closed");
    };
  }, []);
  // The size the sheet opens on: the one tapped in a ladder on the page, else
  // the one they picked last time on this device (the page's, on a page of
  // one piece), else the product's recommended size, so the step that opens
  // is for the size most people order and nobody has to tap a size before
  // paying.
  const remembered = from !== "piece" ? page?.size ?? null : null;
  const has = (size: string | null | undefined) => !!size && sizes.some((s) => s.size === size);
  const [size, setSize] = useState(() =>
    has(initialSize) ? initialSize! : has(remembered) ? remembered! : openingSize(sizes, kind)
  );
  // The page read itself again (a stale-price or stale-piece answer) and the
  // sizes it offers changed: a size it no longer has would be refused on
  // every try, so the one the sheet would open on is picked again.
  const sizeList = sizes.map((s) => s.size).join("|");
  useEffect(() => {
    if (sizes.length && !sizes.some((s) => s.size === size)) setSize(openingSize(sizes, kind));
  }, [sizeList]);
  // Told to the page, which remembers it on the device and names it on the
  // order button. Never for a piece of several: each has its own sheet.
  const onSize = page?.onSize;
  const onStep = page?.onStep;
  // Opened on the form straight away, by the order mail's link or by a
  // return visit that had reached it before: said so to the numbers.
  const remembered0 = useRef(!!payNow);
  useEffect(() => {
    track("checkout.opened", {
      from,
      size,
      step: opensOn,
      visits: page?.visits ?? 0,
      remembered: remembered0.current,
    });
    // Once, as the sheet opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The base the form is made for, fixed as the form is asked for (Continue,
  // or a sheet that opens on the form), the way "Order them together" fixes
  // its lines in `paying`. Taken from the page as it is read, the form would
  // be torn down and quietly remade without the base the moment a re-read
  // stopped offering it (its window closing at midnight while they type
  // their card): the checkout refuses a base out of its window by itself
  // (stale-base), which sends them back to choose, and that is the one way
  // the base leaves a form. Null for a plain one.
  const [payBase, setPayBase] = useState(() =>
    opensOn === "pay" ? chosenBase(page?.bases?.[kept], productKind, size, baseLimits) : null
  );
  const pick = (next: string, how: "pick" | "ladder") => {
    setSize(next);
    track("checkout.size", { size: next, from: how });
    if (from !== "piece") onSize?.(next);
  };
  // Why the form is not there, when it is not.
  const [failed, setFailed] = useState<PayFailure | null>(null);
  // Back on the sizes because the price changed under the form: said above
  // them until they continue.
  const [repriced, setRepriced] = useState(false);
  // Back on the sizes because the checkout refused the base or its message
  // (its window closed meanwhile, or a message the sheet should not have let
  // through): said above them until they continue.
  const [baseRefused, setBaseRefused] = useState(false);
  // The server will not open a checkout for this page again: the sheet is the
  // Etsy one from then on, with no way back to a form that can only fail.
  const [closed, setClosed] = useState(false);
  // Every try is a form of its own, and so a checkout session of its own:
  // "Try again" after a failure has to ask the server again, and the size
  // they already had picked is the one they want.
  const [attempt, setAttempt] = useState(0);
  // What the payment came to. Stripe's "complete" is not money in the bank:
  // a method that pays later completes unpaid, so the sheet asks the server
  // before it thanks anyone for an order, and only says the order is in once
  // Stripe says it was paid.
  const [outcome, setOutcome] = useState<"checking" | "paid" | "processing">("checking");
  // An express to a typed address is one more thing the session is made for
  // (ExpressChoice.tsx), and nothing is added to the key without it. The
  // parcel it is priced for is this size and this many pieces.
  const express = useExpress(delivery, [size], units);
  // The window the thank-you says when the payment was express: the express
  // one, counted from today, which is the day it is paid. Kept here and told
  // to the page, so neither waits for the page to be read again.
  const [paidArrives, setPaidArrives] = useState("");
  const expressArrives = express.pick ? delivery?.expressArrives || t.t("expressArrives") : "";
  const token = page?.token ?? "";
  const onPaid = page?.onPaid;
  const paid = useCallback(
    (session: string) => {
      track("checkout.paid", { size, ...(units > 1 ? { qty: units } : {}), ...(expressArrives ? { express: true } : {}) });
      setOutcome("checking");
      setView("paid");
      setPaidArrives(expressArrives);
      // Told to the page before anything is asked: from here on a reload, or
      // the sheet closed mid-check, must not offer to take the money again.
      onPaid?.("checking", session, expressArrives || undefined);
      const asked = session
        ? checkoutResult(token, session)
        : Promise.resolve<CheckoutResult>("unknown");
      void asked.then((result) => {
        // The checkout finished, so nothing here says it failed: anything
        // short of "paid" is being confirmed, and the receipt says the rest.
        const settled = result === "paid" ? "paid" : "processing";
        setOutcome(settled);
        onPaid?.(settled, session, expressArrives || undefined);
      });
    },
    [size, units, token, onPaid, expressArrives]
  );
  const fail = useCallback((why: PayFailure) => {
    track("checkout.failed", { why });
    if (why === "closed") {
      // Nothing to try again: straight to the way that still works.
      setClosed(true);
      setFailed(null);
      setView("etsy");
      return;
    }
    // The price on the page was not the price any more. The page is reading
    // itself again; back to the sizes, which show the new one, rather than a
    // form that would quietly charge a price they never saw.
    if (why === "repriced") {
      setFailed(null);
      setRepriced(true);
      setView("pick");
      return;
    }
    // The base or its message was refused: back to where they are chosen,
    // which the page reading itself again has brought up to date, rather
    // than a form that would only be refused again.
    if (why === "base") {
      setFailed(null);
      setBaseRefused(true);
      setView("pick");
      return;
    }
    setFailed(why);
  }, []);
  const retry = () => {
    // The form's own file is gone from the server, and asking for it again
    // gets the same nothing: only the page, loaded again, names the new one.
    if (failed === "stale") {
      reloadToPay(pieceId);
      return;
    }
    setFailed(null);
    setAttempt((n) => n + 1);
  };

  async function copyCode() {
    try {
      await navigator.clipboard.writeText(promoCode);
      track("code.copied");
      toast(t.t("copied"), "good");
    } catch {
      /* The code is written on screen either way. */
    }
  }

  const chosen = sizes.find((s) => s.size === size);
  const product = t.t(KIND_KEY[kind ?? pieceKind ?? "minime"] ?? "kindMinime");
  const popular = kind ? TAGGED_SIZE[kind] : null;
  // What the size and how many come to, where the sheet decides how many:
  // the same sum the server makes before it opens the checkout, which it
  // refuses if the total is not this one. Null on a sheet that leaves how
  // many to Stripe's form.
  const format = offer?.format ?? "";
  const billed = owns && chosen ? bill([billIn(chosen, units)]) : null;
  const billTotal = billed ? fill(format, billed.total) : null;
  // The base as it would be sent, or null for the plain one; and whether its
  // message keeps Continue off, too long for this size or not printable.
  const based = chosenBase(baseChoice, productKind, size, baseLimits);
  const baseStuck = baseBlocks(baseChoice, productKind, size, baseLimits);
  // Every form is a checkout session of its own, the base and its words
  // included, like the size and how many: the base as it was fixed when the
  // form was asked for (payBase above).
  const formKey = `${size}:${units}:${payBase ? `b:${payBase.message}` : ""}:${attempt}${express.key ? `:${express.key}` : ""}`;

  /** The way out to the shop, under every step. Etsy's own price only: a
   *  figure in pounds or dollars is never put on the way to a shop that
   *  charges euros. */
  const toEtsy = (
    <button
      className="btn btn-ghost btn-block"
      style={{ marginTop: 8 }}
      onClick={() => setView("etsy")}
    >
      {t.t("payOrEtsy")}
    </button>
  );

  if (view === "paid") {
    return (
      <Sheet
        title={t.t(outcome === "checking" ? "wantTitle" : "paidTitle")}
        onClose={onClose}
        closeLabel={t.t("close")}
        footer={
          <button className="btn btn-primary btn-block" onClick={onClose}>
            {t.t("paidDone")}
          </button>
        }
      >
        {outcome === "checking" ? (
          <div aria-busy="true" style={{ padding: "8px 0" }}>
            <div className="spin" style={{ margin: "0 auto" }} />
          </div>
        ) : (
          <p
            className={outcome === "paid" ? "notice notice-good" : "notice"}
            style={{ margin: 0 }}
          >
            {t.t(outcome === "paid" ? "paidThanks" : "paidProcessing")}
          </p>
        )}
        {outcome !== "checking" && (paidArrives || delivery?.arrives) && (
          <p className="small" style={{ margin: "12px 0 0" }}>
            {paidArrives || delivery?.arrives}
          </p>
        )}
        {/* Paid in the weeks Christmas is no longer promised, while the
            printable card is being sent (the server's PRINTABLE_CARD): that
            it is coming, by email. */}
        {outcome !== "checking" && delivery?.late && delivery.card && (
          <p className="small" style={{ margin: "8px 0 0" }}>
            {t.t("paidCard")}
          </p>
        )}
      </Sheet>
    );
  }

  if (view === "pick") {
    const total = billTotal ?? chosen?.now ?? offer?.now ?? "";
    return (
      <Sheet
        title={t.t("orderTitle")}
        onClose={onClose}
        closeLabel={t.t("close")}
        footer={
          <div style={{ display: "grid", gap: 8 }}>
            <button
              className="btn btn-primary btn-block btn-lift"
              disabled={!chosen || baseStuck}
              onClick={() => {
                if (!chosen || baseStuck) return;
                // The step that matters most: they have seen the size and
                // the total and asked for the form. Remembered, so the next
                // visit's button offers to continue here.
                track("checkout.size", { size, from: "continue", ...(units > 1 ? { qty: units } : {}) });
                if (from !== "piece") {
                  onSize?.(size);
                  onStep?.("pay");
                }
                setFailed(null);
                setRepriced(false);
                setBaseRefused(false);
                setPayBase(based);
                setView("pay");
              }}
            >
              {total ? `${t.t("pickContinue")}\u00a0· ${unbroken(total)}` : t.t("pickContinue")}
            </button>
            <button className="btn btn-ghost btn-block" onClick={() => setView("etsy")}>
              {t.t("payOrEtsy")}
            </button>
          </div>
        }
      >
        {repriced && <p className="notice">{t.t("repriced")}</p>}
        {baseRefused && !repriced && <p className="notice">{t.t("baseRefused")}</p>}
        <div className="pick-head">
          {drawingUrl && <img className="pick-thumb" src={drawingUrl} alt="" />}
          <div className="pick-what">
            <strong>{t.t("pickYours", { product })}</strong>
            {offer?.package && <span className="tiny muted">{offer.package}</span>}
          </div>
        </div>
        {/* Toggle buttons, one of them pressed, rather than a radio group: a
            radio group is announced as one, and whoever hears that reaches for
            the arrow keys, which did nothing here. Tab and Space are what a row
            of buttons promises, and what these do. */}
        <div className="sizepick" role="group" aria-label={t.t("paySize")}>
          {sizes.map((s) => (
            <button
              key={s.size}
              type="button"
              aria-pressed={s.size === size}
              onClick={() => {
                if (s.size !== size) pick(s.size, "pick");
              }}
            >
              <span className="sizepick-size">{s.size}</span>
              <strong className="mono">{s.now}</strong>
              {s.size === popular && <span className="size-tag">{t.t("recommended")}</span>}
            </button>
          ))}
        </div>
        {/* The size, drawn to scale, straight under the chips that choose it,
            not after the bill. */}
        <SizeScale sizes={sizes.map((s) => s.size)} picked={size} language={page?.language ?? "en"} words={t} />
        {/* The Christmas base, under the size it is counted against: for a
            figure, in its window, when paying here. Says nothing otherwise. */}
        {baseLimits && productKind && (
          <ChristmasBase
            kind={productKind}
            size={size}
            sizes={sizes.map((s) => s.size)}
            choice={baseChoice}
            onChange={setBase}
            onSize={(next) => pick(next, "pick")}
            drawingUrl={drawingUrl}
            words={t}
          />
        )}
        {/* How many, under the sizes and their scale, where the sheet decides it. The size
            buttons keep their own prices whatever the number: a price never
            moves while somebody is tapping sizes, and what the extra ones
            cost is said in the bill under it. */}
        {owns && (
          <>
            <div className="qty-row">
              <div>
                <div className="qty-label">{t.t("multiBuyQty")}</div>
                {/* Inside a dated window, with "habitualmente −25%" and its
                    last day (multiBuyWords). */}
                {offer?.multiBuy && (
                  <div className="tiny muted">{multiBuyWords(offer.multiBuy, t, page?.language ?? "en").each}</div>
                )}
              </div>
              <QtyStepper value={units} max={MAX_UNITS} onChange={setQty} words={t} label={t.t("multiBuyQty")} />
            </div>
            {units >= MAX_UNITS && (
              <p className="tiny muted qty-limit">{t.t("qtyLimit", { n: String(MAX_UNITS) })}</p>
            )}
            {billed && billTotal && chosen && offer && units > 1 && (
              <BillBox
                rows={billRows(
                  [{ key: size, name: product, size, chip: chosen, offer, withCode, qty: units }],
                  billed,
                  format,
                  t
                )}
                total={billTotal}
                save={billed.off > 0 ? fill(format, billed.off) : null}
                words={t}
              />
            )}
          </>
        )}
        <div className="pick-lines">
          {delivery?.arrives && <p className="small">{delivery.arrives}</p>}
          {delivery?.christmas && (
            <p className="small" style={{ fontWeight: 600 }}>
              {delivery.christmas}
            </p>
          )}
          <LateLine delivery={delivery} language={page?.language ?? "en"} />
          <p className="tiny muted">
            {/* The full price of one, struck, only beside the price of one:
                beside a total for several it would read as what they were
                all meant to cost. */}
            {chosen && chosen.full !== chosen.now && units === 1 && (
              <>
                <s>{chosen.full}</s>{" "}
              </>
            )}
            {t.t(withCode ? "payNote" : "payNoteFull")}
            {expressNote(t, delivery, page?.language ?? "en")}
            {offer?.vatNote && (
              <>
                <br />
                {offer.vatNote}
              </>
            )}
          </p>
          <p className="tiny muted">{t.t("pickHint")}</p>
          {(mailed === "now" || mailed === "before") && (
            <p className="tiny muted order-mailed" style={{ margin: 0 }}>
              {t.t(mailed === "now" ? "wantMailed" : "wantMailedBefore")}
            </p>
          )}
        </div>
      </Sheet>
    );
  }

  if (view === "pay") {
    return (
      <Sheet title={t.t("orderTitle")} onClose={onClose} closeLabel={t.t("close")}>
        {/* The size they chose, in one line, with the way back to the chips:
            the form below has the screen to itself. */}
        <div className="pay-chosen">
          <span className="small">
            {units > 1 && billTotal ? (
              // Several of it: how many, and what they all come to, with no
              // struck price beside a total.
              <span className="pay-several">
                <strong>
                  {size} ×{units}
                </strong>
                {" · "}
                <strong className="mono">{billTotal}</strong>
              </span>
            ) : (
              <>
                <strong>{size}</strong>
                {chosen && (
                  <>
                    {" · "}
                    {chosen.full !== chosen.now && (
                      <>
                        <s className="muted">{chosen.full}</s>{" "}
                      </>
                    )}
                    <strong className="mono">{chosen.now}</strong>
                  </>
                )}
              </>
            )}
            {offer?.package && <span className="tiny muted"> · {offer.package}</span>}
            {/* "8 cm · Christmas base · “Feliz Natal Avó”": what the form
                below is for, the base and their words included. */}
            {payBase && (
              <span className="tiny pay-base">
                {" · "}
                {t.t("baseName")}
                {payBase.message ? ` · ${t.t("baseQuoted", { message: payBase.message })}` : ""}
              </span>
            )}
          </span>
          {/* Paid for, or being paid: another size is the same answer.
              Where the sheet decides how many, the way back is the way to
              the number too, which is only ever chosen on the first step,
              and so it says so. */}
          {failed !== "ordered" && failed !== "processing" && (
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              onClick={() => {
                setFailed(null);
                setView("pick");
              }}
            >
              {t.t(owns ? "payChangeQty" : "pickChange")}
            </button>
          )}
        </div>
        <p className="tiny muted" style={{ margin: "0 0 12px" }}>
          {t.t(withCode ? "payNote" : "payNoteFull")}
          {expressNote(t, delivery, page?.language ?? "en")}
          {/* On a line of its own: it says what the chip already holds, VAT
              or, in dollars, tax: the form below charges exactly the chip
              and adds nothing to it, in every language. */}
          {offer?.vatNote && (
            <>
              <br />
              {offer.vatNote}
            </>
          )}
          {/* Where the extra pieces took something off, Stripe's form has no
              box for a code (a code and the multi-buy are never both on one
              piece), and a lead with no code of its own would look for one. */}
          {units > 1 && !withCode && billed && billed.off > 0 && offer?.multiBuy && (
            <>
              <br />
              {t.t("multiBuyNoCodes", offVars(offer.multiBuy.percent))}
            </>
          )}
        </p>
        {failed === "ordered" ? (
          <p className="notice notice-good">{t.t("orderedNote")}</p>
        ) : failed === "processing" ? (
          <p className="notice">{t.t("paidProcessing")}</p>
        ) : failed ? (
          <>
            {/* A redraw of theirs still on its way is a minute's wait, not
                a failure: said as a notice, with the same "Try again". */}
            <p className={failed === "drawing" ? "notice" : "notice notice-bad"}>
              {t.t(failed === "drawing" ? "payDrawing" : "payFailed")}
            </p>
            <div className="center">
              <button type="button" className="btn btn-quiet btn-sm" onClick={retry}>
                {t.t(failed === "stale" ? "payReload" : "payRetry")}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Outside the Suspense boundary, so it is there while the
                form's own file is still on its way too. */}
            {upKey !== formKey && (
              <p className="tiny muted center" style={{ margin: "0 0 8px" }}>
                {t.t("payOpening")}
              </p>
            )}
            {(formKeyNow.current = formKey) && null}
            {/* Free delivery or express to a typed address, above the form.
                The form waits, hidden and not rebuilt, only while the express
                fields are open. */}
            <ExpressChoice ex={express} words={t} delivery={delivery} total={format && (billed?.total ?? chosen?.cents) !== undefined ? { minor: (billed?.total ?? chosen?.cents)!, format } : null} />
            <ExpressHold ex={express}>
            <Suspense fallback={<div className="payform payform-loading" />}>
            <PayForm
              key={formKey}
              size={size}
              shown={chosen?.now ?? ""}
              kind={kind}
              pieceId={pieceId}
              // The Christmas base and its message, cleaned, only when one was
              // chosen as the form was asked for: otherwise the request is the
              // one it always was.
              base={payBase?.base}
              message={payBase?.message}
              // How many and the total only where the sheet worked the bill
              // out; otherwise the request is today's and Stripe's form has
              // the quantity.
              qty={owns ? units : undefined}
              total={owns ? (billTotal ?? undefined) : undefined}
              express={express.pick}
              onExpressRefused={express.refused}
              onPaid={paid}
              onFailed={fail}
              onLoaded={formShown}
            />
            </Suspense>
            </ExpressHold>
          </>
        )}
        {/* Not beside a payment that is in or on its way: Etsy would be the
            same order paid for twice. */}
        {failed !== "ordered" && failed !== "processing" && toEtsy}
      </Sheet>
    );
  }

  const etsy = (
    <a
      className="btn btn-primary btn-block btn-lift"
      href={etsyUrl}
      target="_blank"
      rel="noopener"
      onClick={() => {
        // Which of the buttons they came through, which is the last thing
        // this service ever learns before a sale it cannot see.
        track("etsy.clicked", { from, ...(pieceKind ? { kind: pieceKind } : {}) });
        // They are navigating away as this runs, so whatever is queued goes now.
        flush();
      }}
    >
      {offer?.now && inEuros
        ? `${t.t("orderOnEtsy")}\u00a0· ${t.t("fromPrice", { price: unbroken(offer.now) })}`
        : t.t("orderOnEtsy")}
    </a>
  );

  return (
    <Sheet
      title={t.t("orderTitle")}
      onClose={onClose}
      closeLabel={t.t("close")}
      footer={
        payHere && !closed ? (
          <div style={{ display: "grid", gap: 8 }}>
            {etsy}
            <button
              className="btn btn-ghost btn-block"
              onClick={() => {
                // Back to the first step, whatever went wrong with a form
                // before: the next form is asked for afresh.
                setFailed(null);
                setView("pick");
              }}
            >
              {t.t("payInstead")}
            </button>
          </div>
        ) : (
          etsy
        )
      }
    >
      {(mailed === "now" || mailed === "before") && (
        <p className="notice notice-good order-mailed">
          {t.t(mailed === "now" ? "wantMailed" : "wantMailedBefore")}
        </p>
      )}
      <p className="small muted" style={{ margin: "0 0 14px" }}>
        {t.t("wantNow")}
      </p>
      <ol className="howto">
        {steps.map((key, at) => (
          <li className="howto-step" key={key}>
            <span className="howto-num" aria-hidden="true">
              {at + 1}
            </span>
            <div className="howto-body">
              <p className="small" style={{ margin: 0 }}>
                {key === "stepSize" && offer?.package
                  ? t.t("stepSizePackage", { package: offer.package })
                  : t.t(key, {
                      code: promoCode,
                      // A page written before codes carried their percentage
                      // was one of the 30% ones, the only code there was.
                      percent: String(offer?.percent ?? 30),
                    })}
              </p>
              {/* The listing's prices by size, which are euros: left out when
                  the ones in hand are not, rather than shown as Etsy's. */}
              {key === "stepSize" && offer && inEuros && (
                <Ladder
                  price={offer}
                  code={promoCode}
                  kind={kind}
                  picked={payHere ? size : null}
                  words={t}
                  // Where paying here is still there, a size is the way back
                  // to it, on that size.
                  onPick={
                    payHere && !closed
                      ? (picked) => {
                          setFailed(null);
                          pick(picked, "ladder");
                          setView("pick");
                        }
                      : undefined
                  }
                />
              )}
              {key === "stepCode" && (
                <button className="btn btn-quiet btn-sm howto-copy" onClick={() => void copyCode()}>
                  {t.t("copyCode")}
                </button>
              )}
            </div>
          </li>
        ))}
      </ol>
      {/* One group, a step's distance below the last step: 4px under it, the
          date read as part of step 3. */}
      <div className="pick-lines" style={{ marginTop: 18 }}>
        {delivery?.arrives && (
          <p className="small">
            {delivery.arrives}
            {delivery.christmas ? ` ${delivery.christmas}` : ""}
          </p>
        )}
        {/* No card here: an Etsy order is sent it by hand, and Etsy's
            listing does not promise it. */}
        <LateLine delivery={delivery} language={page?.language ?? "en"} card={false} style={{ margin: 0 }} />
        {/* The same line as the price card's, in every currency. */}
        <p className="small muted">{t.t("duties")}</p>
        {/* Etsy's listing has no Christmas base: said where they would go
            there instead, while the page offers one for this figure. */}
        {baseLimits && productKind && takesBase(productKind) && !closed && (
          <p className="small muted">{t.t("baseNotOnEtsy")}</p>
        )}
      </div>
    </Sheet>
  );
}
